import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { add } from 'date-fns';
import { IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';

import { SimpleTable } from '../../molecules/simple-table';
import { useSelector } from '../../store/utils';
import { Group } from '../../organisms/group';
import { FormattedDateTime } from '../../utils/formatted-datetime';
import { useFormatNumber } from '../../utils/format-number';
import { jsDateToIsoDate } from '../../utils/isodate';
import { trpc } from '../../api/trpc';
import { Row } from '../../atoms/row';

import { RfidCardCreate } from '../../organisms/rfid/rfid-card-create';
import { RfidCardDelete } from '../../organisms/rfid/rfid-card-delete';
import { RfidCardEdit } from '../../organisms/rfid/rfid-card-edit';

const defaultFrom = add(new Date(), { months: -1 });
const defaultTo = new Date();

export const RfidCards = () => {
  const rfidCards = useSelector((state) => state.variables.RFID_CARDS);
  const formatNumber = useFormatNumber();

  const [from, setFrom] = useState<Date | null>(defaultFrom);
  const [to, setTo] = useState<Date | null>(defaultTo);

  const { data: consumptionByCard, isLoading } =
    trpc.wallbox.consumptionByCard.useQuery(
      {
        from: jsDateToIsoDate(from ?? defaultFrom),
        to: jsDateToIsoDate(to ?? defaultTo),
      },
      { enabled: Boolean(from && to), networkMode: 'always' }
    );

  const entries = Object.entries(rfidCards ?? {});

  const formatKwh = (value: number) =>
    formatNumber(value, {
      style: 'unit',
      unit: 'kilowatt-hour',
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    });

  const handleExport = () => {
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;

    const csv = [
      ['ID', 'Name', 'Consumption (kWh)'],
      ...entries.map(([id, { name }]) => [
        id,
        name,
        String(consumptionByCard?.[id] ?? 0),
      ]),
    ]
      .map((row) => row.map(escape).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `rfid-consumption_${(from ?? defaultFrom).toISOString().slice(0, 10)}_${(
        to ?? defaultTo
      )
        .toISOString()
        .slice(0, 10)}.csv`
    );
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Group
      layout="list"
      title={
        <FormattedMessage id="rfid_cards.title" defaultMessage="RFID karty" />
      }
    >
      <Paper>
        <Stack direction="row" gap={1} sx={{ px: 2, pt: 2, pb: 1 }}>
          <DatePicker
            label={
              <FormattedMessage id="rfid_cards.from" defaultMessage="Od" />
            }
            // eslint-disable-next-line
            // @ts-ignore
            value={from}
            onChange={(value) => setFrom(value)}
            maxDate={to ?? undefined}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label={<FormattedMessage id="rfid_cards.to" defaultMessage="Do" />}
            // eslint-disable-next-line
            // @ts-ignore
            value={to}
            onChange={(value) => setTo(value)}
            minDate={from ?? undefined}
            slotProps={{ textField: { size: 'small' } }}
          />

          <Tooltip
            title={
              <FormattedMessage
                id="rfid_cards.export"
                defaultMessage="Exportovat CSV"
              />
            }
          >
            <span style={{ marginLeft: 'auto' }}>
              <IconButton
                onClick={handleExport}
                disabled={entries.length === 0}
                size="small"
              >
                <Download />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <SimpleTable
          firstLineBold
          rows={[
            [
              <FormattedMessage
                key={0}
                id="rfid_cards.col.id"
                defaultMessage="ID karty"
              />,
              <FormattedMessage
                key={1}
                id="rfid_cards.col.name"
                defaultMessage="Název"
              />,
              <FormattedMessage
                key={2}
                id="rfid_cards.col.consumption"
                defaultMessage="Spotřeba"
              />,
              <FormattedMessage
                key={3}
                id="rfid_cards.col.created_at"
                defaultMessage="Přidáno"
              />,
              <></>,
            ],
            ...entries.map(([id, { name, createdAt }]) => [
              id,
              name,
              isLoading ? '-' : formatKwh(consumptionByCard?.[id] ?? 0),
              <FormattedDateTime key="createdAt" value={createdAt} />,
              <Row key="actions">
                <RfidCardEdit key="edit" id={id} name={name} />
                <RfidCardDelete key="delete" id={id} name={name} />
              </Row>,
            ]),
          ]}
          noPaper
        />

        {entries.length === 0 && (
          <Typography sx={{ textAlign: 'center', p: 2 }}>
            <FormattedMessage
              id="rfid_cards.no_cards"
              defaultMessage="Žádné karty nebyly přidány"
            />
          </Typography>
        )}

        <RfidCardCreate sx={{ mx: 'auto' }} />
      </Paper>
    </Group>
  );
};
